import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import * as routes from '../utils/routes';
import '../index.css';
import apiClient from '../services/apiClient';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DashboardIcon from '../assets/svgs/DashboardIcon';
import ProductIcon from '../assets/svgs/ProductIcon';
import ExpenseIcon from '../assets/svgs/ExpenseIcon';
import OrderIcon from '../assets/svgs/OrderIcon';
import InventoryIcon from '../assets/svgs/InventoryIcon';
import LogoutIcon from '../assets/svgs/LogoutIcon';
import { useSelector } from 'react-redux';
import { IconContainer } from './SharedComponents';
import UsersIcon from '../assets/svgs/UsersIcon';
import SettingsIcon from '../assets/svgs/SettingsIcon';
import BusinessIcon from '../assets/svgs/BusinessIcon';
import CustomerIcon from '../assets/svgs/CustomerIcon';
import SubscriptionIcon from '../assets/svgs/SubscriptionIcon';

const Bar = styled.div<{ $isOpen: boolean }>`
	position: fixed;
	top: 0;
	left: 0;
	background-color: white;
	width: 250px;
	min-height: 100vh;
	overflow: auto;
	display: flex;
	flex-direction: column;
	padding: 10px 20px;
	padding-top: 40px;
	box-sizing: border-box;
	box-shadow: 0px 3px 4px 0px rgb(221 232 235 / 61%);
	border-bottom: 1px solid #dfedf3;
	z-index: 1; /* Set lower than Header */
	transition: transform 300ms cubic-bezier(0.25, 1.3, 0.5, 1);

	@media (max-width: 768px) {
		transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(-100%)')};
		width: 100%;
	}

	@media (min-width: 768px) {
		transform: none; /* Always visible for larger screens */
	}
`;

const Header = styled.header`
	position: fixed;
	width: 100vw;
	display: flex;
	z-index: 3; /* Set higher than Bar */
	@media (min-width: 768px) {
		display: none; /* Hide button for larger screens */
	}
`;

const ToggleButton = styled.button`
	z-index: 1000;
	color: black;
	border: 1px solid #616265;
	border-radius: 4px;
	padding: 6px 12px;
	cursor: pointer;

	@media (min-width: 768px) {
		display: none; /* Hide button for larger screens */
	}
`;

const Logo = styled.p`
	font-size: 25px;
	font-family: 'Noto Sans', sans-serif;
	font-family: 'Red Hat Display', sans-serif;
	color: black;
	@media (max-width: 768px) {
		display: none; /* Hide logo for smaller screens */
	}
`;

const NavItems = styled.div`
	display: flex;
	flex-direction: column;
`;

const NavItem = styled.div<{ $isActive: boolean }>`
	display: flex;
	flex-direction: row;
	align-items: center;
	font-size: 14px;
	text-decoration: none;
	color: ${(props) => (props.$isActive ? '#3a5bd8' : '#7E818B')};
	background-color: ${(props) => (props.$isActive ? 'rgba(58, 91, 216, 0.05)' : 'transparent')};
	padding: 10px 25px;
	margin-bottom: 5px;
	border-radius: 15px;
	width: 100%;
	box-sizing: border-box;
	transition: all 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
	cursor: pointer;
	transform: scale(1);

	&:hover {
		color: #3a5bd8;
		background-color: rgba(58, 91, 216, 0.08);
		transform: scale(1.02);

		${IconContainer} svg {
			fill: #3a5bd8;
		}
	}

	&:active {
		transform: scale(0.98);
		transition: transform 100ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
	}
`;

const SubMenuWrapper = styled.div<{ $isExpanded: boolean }>`
	max-height: ${(props) => (props.$isExpanded ? '500px' : '0')};
	overflow: hidden;
	opacity: ${(props) => (props.$isExpanded ? '1' : '0')};
	transform: ${(props) => (props.$isExpanded ? 'translateY(0)' : 'translateY(-10px)')};
	transition: max-height 450ms cubic-bezier(0.4, 0, 0.2, 1), opacity 300ms cubic-bezier(0.4, 0, 0.2, 1),
		transform 350ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const SubMenuItem = styled.div<{ $isActive: boolean; $index?: number }>`
	display: flex;
	flex-direction: row;
	align-items: center;
	font-size: 13px;
	text-decoration: none;
	color: ${(props) => (props.$isActive ? '#3a5bd8' : '#7E818B')};
	background-color: transparent;
	padding: 8px 25px 8px 55px;
	margin-bottom: 3px;
	border-radius: 10px;
	width: 100%;
	box-sizing: border-box;
	transition: all 250ms cubic-bezier(0.4, 0, 0.2, 1);
	transition-delay: ${(props) => (props.$index || 0) * 35}ms;
	opacity: ${(props) => (props.$isActive ? 1 : 0.8)};
	transform: translateX(0) translateY(0);

	&:hover {
		color: #3a5bd8;
		background-color: #f5f8ff;
		opacity: 1;
		transform: translateX(4px);
		box-shadow: 0 1px 3px rgba(58, 91, 216, 0.08);
		transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
	}
`;

const ExpandableNavItem = styled.div<{ $isActive: boolean }>`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	font-size: 14px;
	text-decoration: none;
	color: ${(props) => (props.$isActive ? '#3a5bd8' : '#7E818B')};
	background-color: ${(props) => (props.$isActive ? 'rgba(58, 91, 216, 0.05)' : 'transparent')};
	padding: 10px 25px;
	margin-bottom: 5px;
	border-radius: 15px;
	width: 100%;
	box-sizing: border-box;
	transition: all 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
	cursor: pointer;
	transform: scale(1);

	&:hover {
		color: #3a5bd8;
		background-color: rgba(58, 91, 216, 0.08);
		transform: scale(1.02);

		${IconContainer} svg {
			fill: #3a5bd8;
		}
	}

	&:active {
		transform: scale(0.98);
		transition: transform 100ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
	}
`;

const ExpandIcon = styled.div<{ $isExpanded: boolean }>`
	transform: rotate(${(props) => (props.$isExpanded ? '180deg' : '0deg')});
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 18px;
	color: ${(props) => (props.$isExpanded ? '#3a5bd8' : 'inherit')};
	transition: transform 350ms cubic-bezier(0.4, 0, 0.2, 1), color 200ms ease-out;

	svg {
		font-size: 18px;
		width: 18px;
		height: 18px;
	}
`;

const LogoutButton = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	font-size: 14px;
	text-decoration: none;
	color: #7e818b;
	padding: 15px 30px;
	margin-bottom: 5px;
	border-radius: 15px;
	width: 100%;
	box-sizing: border-box;
	transition: background-color 200ms, color 100ms;
	user-select: none;
	cursor: pointer;

	&:hover {
		color: #3a5bd8;

		${IconContainer} svg {
			fill: #3a5bd8;
		}
	}
`;

const styles = {
	logoLink: {
		width: '100%',
		display: 'flex',
		flexDirection: 'row' as 'row',
		alignItems: 'center',
		justifyContent: 'center',
		marginBottom: '40px',
		textDecoration: 'none',
		fontSize: '35px',
		color: 'black',
	},
	link: {
		fontSize: '14px',
		textDecoration: 'none',
		color: '#7E818B',
	},
};

interface NavItemProp {
	label: string;
	route: string;
	icon?: any;
	subItems?: NavItemProp[];
}

export default function NavBar() {
	const [isOpen, setIsOpen] = useState(false);
	const [expandedItems, setExpandedItems] = useState<string[]>([]);

	const location = useLocation();
	const user = useSelector((state: any) => state.auth.user);

	const navSections = [
		[
			{ label: 'Dashboard', route: routes.DASHBOARD, icon: DashboardIcon },
			{ label: 'Products', route: routes.PRODUCTS, icon: ProductIcon },
			{ label: 'Inventory', route: routes.INVENTORY, icon: InventoryIcon },
			{ label: 'Orders', route: routes.ORDERS, icon: OrderIcon },
			{ label: 'Customers', route: routes.CUSTOMERS, icon: CustomerIcon },
			{ label: 'Expenses', route: routes.EXPENSES, icon: ExpenseIcon },
		],
		[
			{ label: 'Businesses', route: routes.SHOP_SELECTOR, icon: BusinessIcon },
			{ label: 'Manage Users', route: routes.USER_MANAGEMENT, icon: UsersIcon },
			// { label: "Activity Logs", route: routes.ACTIVITY_LOGS, icon: ActivityLogIcon },
			...(user?.is_owner ? [{ label: 'Subscription', route: routes.SUBSCRIPTION, icon: SubscriptionIcon }] : []),
			{ label: 'Account Settings', route: routes.ACCOUNT, icon: SettingsIcon },
		],
	];

	const handleLogout = () => {
		apiClient.auth.logout();
	};

	const toggleNavBar = () => {
		setIsOpen(!isOpen);
	};

	const toggleExpandedItem = (route: string) => {
		setExpandedItems((prev) => (prev.includes(route) ? prev.filter((item) => item !== route) : [...prev, route]));
	};

	const handleLinkClick = () => {
		if (window.innerWidth <= 768) {
			setIsOpen(false); // Close the NavBar on small screens
		}
	};

	return (
		<>
			<Header
				className="p-2 flex justify-between items-center"
				style={{ backgroundColor: 'white', padding: '10px 20px' }}
			>
				<div className="text-black" style={{ fontWeight: 'bold', fontSize: '19px' }}>
					Vend<span style={{ color: '#3a5bd9' }}>Ready</span>
				</div>
				<ToggleButton className="text-black font-bold py-2 px-4 rounded" onClick={toggleNavBar}>
					{isOpen ? (
						<>
							<CloseIcon style={{ color: 'black', fontSize: '20px' }} />
							<span style={{ color: 'black', fontWeight: '200', fontSize: '13px', marginLeft: '6px' }}>
								Close
							</span>
						</>
					) : (
						<>
							<MenuIcon style={{ color: 'black', fontSize: '20px' }} />
							<span style={{ color: 'black', fontWeight: '200', fontSize: '13px', marginLeft: '6px' }}>
								Menu
							</span>
						</>
					)}
				</ToggleButton>
			</Header>

			<Bar $isOpen={isOpen}>
				<Link to={routes.DASHBOARD} style={styles.logoLink} onClick={handleLinkClick}>
					<Logo>
						Vend<span style={{ color: '#3a5bd9' }}>Ready</span>
					</Logo>
				</Link>

				<NavItems>
					{navSections.map((section: NavItemProp[], index: number) => (
						<div key={index}>
							{section.map((navItem: NavItemProp) => {
								const IconComponent = navItem.icon;
								const isActive = Boolean(
									location.pathname === navItem.route ||
										(navItem.subItems &&
											navItem.subItems.some((subItem) => location.pathname === subItem.route))
								);
								const isExpanded = expandedItems.includes(navItem.route);

								return (
									<React.Fragment key={navItem.route}>
										{navItem.subItems ? (
											<div>
												<ExpandableNavItem
													$isActive={isActive}
													onClick={() => toggleExpandedItem(navItem.route)}
												>
													<div style={{ display: 'flex', alignItems: 'center' }}>
														<IconContainer $isActive={isActive}>
															<IconComponent />
														</IconContainer>
														<span style={{ marginLeft: '15px' }}>{navItem.label}</span>
													</div>
													<ExpandIcon $isExpanded={isExpanded}>
														<KeyboardArrowDownIcon />
													</ExpandIcon>
												</ExpandableNavItem>
												<SubMenuWrapper $isExpanded={isExpanded}>
													{navItem.subItems.map((subItem: NavItemProp, index: number) => {
														const subIsActive = Boolean(
															location.pathname === subItem.route
														);
														return (
															<Link
																key={subItem.route}
																to={subItem.route}
																style={styles.link}
																onClick={handleLinkClick}
															>
																<SubMenuItem $isActive={subIsActive} $index={index}>
																	<span>{subItem.label}</span>
																</SubMenuItem>
															</Link>
														);
													})}
												</SubMenuWrapper>
											</div>
										) : (
											<Link to={navItem.route} style={styles.link} onClick={handleLinkClick}>
												<NavItem $isActive={isActive}>
													<IconContainer $isActive={isActive}>
														<IconComponent />
													</IconContainer>
													<span style={{ marginLeft: '15px' }}>{navItem.label}</span>
												</NavItem>
											</Link>
										)}
									</React.Fragment>
								);
							})}
							<div
								style={{
									margin: '15px auto',
									height: '1px',
									backgroundColor: '#7E818B',
									width: '70%',
									opacity: '0.4',
								}}
							></div>
						</div>
					))}
				</NavItems>
				<LogoutButton onClick={handleLogout}>
					<IconContainer $isActive={false}>
						<LogoutIcon width={15} />
					</IconContainer>
					<span style={{ marginLeft: '20px' }}>Logout</span>
				</LogoutButton>
				{user && (
					<div
						style={{
							textAlign: 'center',
							marginBottom: '20px',
							fontSize: '14px',
							color: '#666',
							padding: '8px 15px',
							backgroundColor: '#f5f8ff',
							borderRadius: '6px',
							border: '1px solid #e0e7ff',
							display: 'flex',
							flexDirection: 'row',
						}}
					>
						<UsersIcon />
						<span>
							<span style={{ marginLeft: '10px' }}>{user.email}</span>
						</span>
					</div>
				)}
			</Bar>
		</>
	);
}
